import { Component, Input, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormControl, Validators, AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

type FrequencyType = 'MINUTES' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM';

export interface ScheduleFormModel {
  frequencyType: FormControl<FrequencyType | null>;
  intervalValue: FormControl<number | null>;
  startTime: FormControl<string | null>;
  daysOfWeek: FormControl<string[] | null>;
  daysOfMonth: FormControl<number[] | null>;
  cronExpression: FormControl<string | null>;
  timezone: FormControl<string | null>;
}

@Component({
  selector: 'app-frequency-schedule',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './frequency-schedule.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FrequencySchedulerComponent implements OnInit, OnDestroy {

  static createScheduleForm(fb: FormBuilder, defaultTimezone: string = 'UTC'): FormGroup<ScheduleFormModel> {
    return fb.group<ScheduleFormModel>({
      frequencyType: fb.control('DAILY', Validators.required),
      intervalValue: fb.control(1),
      startTime: fb.control('08:00', Validators.required),
      daysOfWeek: fb.control<string[]>([]),
      daysOfMonth: fb.control<number[]>([1]),
      cronExpression: fb.control('0 0 8 * * *'),
      timezone: fb.control(defaultTimezone, Validators.required)
    });
  }

  @Input({ required: true }) form!: FormGroup<ScheduleFormModel>;
  @Input() timezones: string[] = [];

  frequencyOptions: Array<{ value: FrequencyType; label: string; description: string; svgIcon: string }> = [
    { value: 'HOURLY', label: 'Cada hora', description: 'Actualiza cada X horas', svgIcon: 'clock' },
    { value: 'DAILY', label: 'Diariamente', description: 'Se ejecuta una vez al día', svgIcon: 'calendar-day' },
    { value: 'WEEKLY', label: 'Semanalmente', description: 'En días específicos de la semana', svgIcon: 'calendar-week' },
    { value: 'MONTHLY', label: 'Mensualmente', description: 'Un día específico del mes', svgIcon: 'calendar-month' },
    { value: 'CUSTOM', label: 'Personalizado', description: 'Constructor visual avanzado', svgIcon: 'settings' },
  ];

  hourlyOptions = [1, 2, 3, 4, 6, 8, 12, 24];
  daysOfWeekOptions = [
    { value: 'MON', label: 'Lunes' },
    { value: 'TUE', label: 'Martes' },
    { value: 'WED', label: 'Miércoles' },
    { value: 'THU', label: 'Jueves' },
    { value: 'FRI', label: 'Viernes' },
    { value: 'SAT', label: 'Sábado' },
    { value: 'SUN', label: 'Domingo' }
  ];

  private destroy$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    if (!this.form) throw new Error('FrequencySchedulerComponent requiere un [form] válido.');
    this.assertRequiredControls();

    //Sincroniza validadores y cron cuando cambia la frecuencia
    this.form.controls.frequencyType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidators();
        this.syncCronExpression();
        this.cdr.markForCheck();
      });

    // Sincroniza cron cuando cambian otros campos relevantes                            
    ['startTime', 'intervalValue', 'daysOfMonth', 'daysOfWeek'].forEach(field => {
      this.form.get(field)!.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.syncCronExpression();
          this.cdr.markForCheck();
        });
    });

    //Aplica validadores iniciales segun el valor por defecto
    this.updateConditionalValidators();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // =============================
  // GETTERS
  // =============================

  get currentFrequency(): FrequencyType | null {
    return this.form.controls.frequencyType.value;
  }

  get selectedDays(): string[] {
    return this.form.controls.daysOfWeek.value ?? [];
  }

  get selectedFrequencyOption() {
    return this.frequencyOptions.find(o => o.value === this.currentFrequency);
  }

  get nextExecutionSummary(): string {
    const tz = this.form.controls.timezone.value || 'UTC';
    const time = this.form.controls.startTime.value || '08:00';

    switch (this.currentFrequency) {
      case 'HOURLY':  return `Cada ${this.form.controls.intervalValue.value || 1} hora(s)`;
      case 'DAILY':   return `Diariamente a las ${time} (${tz})`;
      case 'WEEKLY':  return `Los días seleccionados a las ${time} (${tz})`;
      case 'MONTHLY': return `Los días ${(this.form.controls.daysOfMonth.value || [1]).join(', ')} a las ${time} (${tz})`;
      case 'CUSTOM':  return 'CRON manual configurado';
      default:        return 'Configuración no válida';
    }
  }


  // ============================
  // METODOS
  // ============================

  setFrequencyType(type: FrequencyType): void {
    // 1. Actualizamos el estado. Angular se encarga de cambiar el HTML automáticamente.
    this.form.controls.frequencyType.setValue(type);
    // 2. Sincronizamos la expresión CRON por si acaso
    this.syncCronExpression();
  }

  isDaySelected(day: string): boolean {
    return this.selectedDays.includes(day);
  }

  get monthlyDays(): number[] {
    return this.form.controls.daysOfMonth.value ?? [];
  }

  addMonthDay(input: HTMLInputElement): void {
    const day = parseInt(input.value, 10);
    if (!day || day < 1 || day > 31) return;
    const days = [...this.monthlyDays];
    if (!days.includes(day)) {
      days.push(day);
      days.sort((a, b) => a - b);
      this.form.controls.daysOfMonth.setValue(days);
    }
    input.value = '';
  }

  removeMonthDay(day: number): void {
    this.form.controls.daysOfMonth.setValue(this.monthlyDays.filter(d => d !== day));
  }

  toggleDay(day: string): void {
    const days = [...this.selectedDays];
    const index = days.indexOf(day);

    if (index === -1) days.push(day);
    else days.splice(index, 1);

    this.form.controls.daysOfWeek.setValue(days);
  }

  // ===========================
  // METODOS PRIVADOS
  // ===========================

  private assertRequiredControls(): void {
    const required = ['frequencyType', 'intervalValue', 'startTime', 'daysOfWeek',
      'daysOfMonth', 'cronExpression', 'timezone'];
    const missing = required.filter(c => !this.form.get(c));
    if (missing.length) throw new Error(`FrequencySchedulerComponent: faltan controles: ${missing.join(', ')}`);
  }

  private updateConditionalValidators(): void {
    const type = this.currentFrequency;
    const c = this.form.controls;

    // intervalValue: MINUTES (1..59) | HOURLY (1..23)
    if (type === 'MINUTES') {
      this.setValidators(c.intervalValue, [Validators.required, Validators.min(1), Validators.max(59)]);
    } else if (type === 'HOURLY') {
      this.setValidators(c.intervalValue, [Validators.required, Validators.min(1), Validators.max(23)]);
    } else {
      this.setValidators(c.intervalValue, []);
    }

    // daysOfMonth: requerido en MONTHLY (al menos 1 día)
    this.setValidators(c.daysOfMonth, type === 'MONTHLY' ? [this.minLengthArray(1)] : []);

    // cronExpression: requerido solo en CUSTOM
    this.setValidators(c.cronExpression, type === 'CUSTOM' ? [Validators.required] : []);

    // daysOfWeek: requerido en WEEKLY (al menos 1 día)
    this.setValidators(c.daysOfWeek, type === 'WEEKLY' ? [this.minLengthArray(1)] : []);
  }

  private minLengthArray(min: number) {
    return (control: AbstractControl) => {
      const arr = control.value;
      return Array.isArray(arr) && arr.length >= min ? null : { minLengthArray: { required: min, actual: Array.isArray(arr) ? arr.length : 0 } };
    };
  }

  private setValidators(control: AbstractControl, validators: any[]): void {
    control.setValidators(validators.length ? validators : null);
    control.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Genera expresión cron de 6 campos (formato Spring):
   * segundo minuto hora díaMes mes díaSemana
   */
  syncCronExpression(): void {
    const type = this.currentFrequency;
    if (type === 'CUSTOM') return;

    const time = this.form.controls.startTime.value || '08:00';
    const [h, m] = time.split(':').map(Number);
    let cron = '';

    switch (type) {
      case 'HOURLY':
        cron = `0 0 */${this.form.controls.intervalValue.value || 1} * * *`;
        break;
      case 'DAILY':
        cron = `0 ${m} ${h} * * *`;
        break;
      case 'WEEKLY':
        const days = this.selectedDays.join(',') || '*';
        cron = `0 ${m} ${h} * * ${days}`;
        break;
      case 'MONTHLY':
        cron = `0 ${m} ${h} ${(this.form.controls.daysOfMonth.value || [1]).join(',')} * *`;
        break;
    }

    if (cron && this.form.controls.cronExpression.value !== cron) {
      this.form.controls.cronExpression.setValue(cron, { emitEvent: false });
    }
  }

}
